import { Component, HostBinding, HostListener, Inject, Injectable, Input, NgModule, OnDestroy, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { CommonModule, NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterLink, RouterLinkActive } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface INavData {
  name?: string;
  url?: string | any[];
  icon?: string;
  badge?: { text: string; variant?: string };
  title?: boolean;
  children?: INavData[];
  variant?: string;
  attributes?: { [key: string]: any };
  divider?: boolean;
  class?: string;
  label?: { class?: string; variant?: string };
  wrapper?: { element: string; attributes?: { [key: string]: any } };
  linkProps?: {
    queryParams?: any;
    fragment?: string;
    queryParamsHandling?: any;
    preserveFragment?: boolean;
    skipLocationChange?: boolean;
    replaceUrl?: boolean;
    state?: any;
    routerLinkActive?: string;
    routerLinkActiveOptions?: { exact?: boolean };
  };
  href?: string;
}

@Injectable({ providedIn: 'root' })
export class SidebarStateService {
  private minimizedSubject = new BehaviorSubject<boolean>(false);
  readonly minimized$ = this.minimizedSubject.asObservable();

  get value(): boolean {
    return this.minimizedSubject.value;
  }

  setMinimized(value: boolean): void {
    this.minimizedSubject.next(value);
  }

  toggle(): void {
    this.setMinimized(!this.value);
  }
}

@Component({
    selector: 'app-header',
    template: `<ng-content></ng-content>`,
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Input() fixed = false;
  // Placeholder inputs retained for compatibility with CoreUI templates.
  @Input() navbarBrandRouterLink?: string | any[];
  @Input() navbarBrandMinimized?: { src: string; width?: number; height?: number; alt?: string };
  @Input() sidebarToggler?: boolean | string;
  @Input() asideMenuToggler?: boolean | string;
  @Input() mobileAsideMenuToggler?: boolean | string;
  @HostBinding('class.app-header') appHeaderClass = true;
  @HostBinding('class.navbar') navbarClass = true;
  private readonly fixedClass = 'header-fixed';

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {}

  ngOnInit(): void {
    if (this.fixed) {
      this.renderer.addClass(this.document.body, this.fixedClass);
    }
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.fixedClass);
  }
}



@Component({
    selector: 'app-footer',
    template: `<ng-content></ng-content>`,
})
export class AppFooterComponent implements OnInit, OnDestroy {
  @Input() fixed = false;
  @HostBinding('class.app-footer') appFooterClass = true;
  private readonly fixedClass = 'footer-fixed';

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {}

  ngOnInit(): void {
    if (this.fixed) {
      this.renderer.addClass(this.document.body, this.fixedClass);
    }
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.fixedClass);
  }
}



@Component({
    selector: 'app-sidebar',
    template: `<ng-content></ng-content>`,
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  @Input() fixed = false;
  @Input() display: string | boolean = '';
  @Input() offCanvas = false;
  @HostBinding('class.sidebar') sidebarClass = true;
  private subscription: Subscription | null = null;

  constructor(
    private sidebarState: SidebarStateService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.applyBodyClasses();
    this.subscription = this.sidebarState.minimized$.subscribe(value => this.setMinimizedClasses(value));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.setMinimizedClasses(false);
    if (this.fixed) {
      this.renderer.removeClass(this.document.body, 'sidebar-fixed');
    }
    const cssClass = this.getDisplayClass();
    if (cssClass) {
      this.renderer.removeClass(this.document.body, cssClass);
    }
  }

  private applyBodyClasses(): void {
    if (this.fixed) {
      this.renderer.addClass(this.document.body, 'sidebar-fixed');
    }
    const cssClass = this.getDisplayClass();
    if (cssClass) {
      this.renderer.addClass(this.document.body, cssClass);
    }
    if (this.offCanvas) {
      this.renderer.addClass(this.document.body, 'sidebar-off-canvas');
    }
    this.setMinimizedClasses(this.sidebarState.value);
  }

  private getDisplayClass(): string | null {
    if (!this.display) {
      return null;
    }
    return this.display === true ? 'sidebar-show' : `sidebar-${this.display}-show`;
  }

  private setMinimizedClasses(minimized: boolean): void {
    if (minimized) {
      this.renderer.addClass(this.document.body, 'sidebar-minimized');
      this.renderer.addClass(this.document.body, 'brand-minimized');
    } else {
      this.renderer.removeClass(this.document.body, 'sidebar-minimized');
      this.renderer.removeClass(this.document.body, 'brand-minimized');
    }
  }
}

@Component({
    selector: 'app-sidebar-footer',
    template: `<ng-content></ng-content>`,
})
export class AppSidebarFooterComponent {
  @HostBinding('class.sidebar-footer') sidebarFooterClass = true;
}

@Component({
    selector: 'app-sidebar-form',
    template: `<ng-content></ng-content>`,
})
export class AppSidebarFormComponent {
  @HostBinding('class.sidebar-form') sidebarFormClass = true;
}

@Component({
    selector: 'app-sidebar-header',
    template: `<ng-content></ng-content>`,
})
export class AppSidebarHeaderComponent {
  @HostBinding('class.sidebar-header') sidebarHeaderClass = true;
}

@Component({
    selector: 'app-sidebar-minimizer',
    template: `<button class="sidebar-minimizer" type="button"></button>`,
})
export class AppSidebarMinimizerComponent {
  constructor(private sidebarState: SidebarStateService) {}

  @HostListener('click')
  toggle(): void {
    this.sidebarState.toggle();
  }
}

@Component({
    selector: 'app-sidebar-nav',
    template: `
    <nav class="sidebar-nav" role="navigation">
      <ul class="nav">
        <ng-container *ngTemplateOutlet="itemTemplate; context: { items: navItems }"></ng-container>
      </ul>
    </nav>
    
    <ng-template #itemTemplate let-items="items">
      @for (item of items; track item) {
        @if (item.divider) {
          <li class="nav-divider"></li>
        }
        @if (item.title) {
          <li class="nav-title">{{ item.name }}</li>
        }
        @if (!item.divider && !item.title) {
          <li [ngClass]="getItemClasses(item)" [class.open]="isOpen(item)">
            @if (item.children?.length) {
              <a class="nav-link nav-dropdown-toggle" href (click)="toggleDropdown(item, $event)">
                @if (item.icon) {
                  <i [ngClass]="item.icon"></i>
                }
                {{ item.name }}
                @if (item.badge) {
                  <span class="badge" [ngClass]="getBadgeClass(item)">{{ item.badge.text }}</span>
                }
              </a>
              <ul class="nav-dropdown-items">
                <ng-container *ngTemplateOutlet="itemTemplate; context: { items: item.children }"></ng-container>
              </ul>
            } @else {
              <a
                class="nav-link"
                [ngClass]="{ disabled: item.attributes?.disabled }"
                [routerLink]="item.url"
                [routerLinkActive]="item.linkProps?.routerLinkActive || 'active'"
                [routerLinkActiveOptions]="item.linkProps?.routerLinkActiveOptions || { exact: false }"
                [attr.target]="item.attributes?.target"
                [attr.href]="item.href"
                >
                @if (item.icon) {
                  <i [ngClass]="item.icon"></i>
                }
                {{ item.name }}
                @if (item.badge) {
                  <span class="badge" [ngClass]="getBadgeClass(item)">{{ item.badge.text }}</span>
                }
              </a>
            }
          </li>
        }
      }
    </ng-template>
    `,
    imports: [
        NgTemplateOutlet,
        NgClass,
        RouterLink,
        RouterLinkActive,
    ],
})
export class AppSidebarNavComponent {
  @Input() navItems: INavData[] = [];
  private openMap = new WeakMap<INavData, boolean>();

  constructor(private router: Router) {}

  toggleDropdown(item: INavData, event: MouseEvent): void {
    event.preventDefault();
    this.openMap.set(item, !this.isExplicitlyOpen(item));
  }

  isOpen(item: INavData): boolean {
    return this.isExplicitlyOpen(item) || this.hasActiveChild(item);
  }

  private isExplicitlyOpen(item: INavData): boolean {
    return !!this.openMap.get(item);
  }

  private hasActiveChild(item: INavData): boolean {
    if (!item.children) {
      return false;
    }
    return item.children.some(child => this.isRouteActive(child));
  }

  private isRouteActive(item: INavData): boolean {
    if (typeof item.url === 'string') {
      return this.router.isActive(item.url, !!item.linkProps?.routerLinkActiveOptions?.exact);
    }
    if (Array.isArray(item.url)) {
      try {
        const tree = this.router.createUrlTree(item.url);
        return this.router.isActive(tree, !!item.linkProps?.routerLinkActiveOptions?.exact);
      } catch {
        return false;
      }
    }
    return false;
  }

  getItemClasses(item: INavData): Record<string, boolean> {
    const classes: Record<string, boolean> = { 'nav-item': true };
    if (item.children?.length) {
      classes['nav-dropdown'] = true;
    }
    if (item.class) {
      classes[item.class] = true;
    }
    return classes;
  }

  getBadgeClass(item: INavData): string {
    return item.badge?.variant ? `badge-${item.badge.variant}` : 'badge-info';
  }
}



@Injectable({ providedIn: 'root' })
export class AppBreadcrumbService {
  private breadcrumbSubject = new BehaviorSubject<Array<{ label: any; url: string }>>([]);
  readonly breadcrumbs = this.breadcrumbSubject.asObservable();

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const breadcrumbs: Array<{ label: any; url: string }> = [];
      let currentRoute: ActivatedRoute | null = this.route.root;
      let url = '';
      while (currentRoute) {
        const children = currentRoute.children;
        currentRoute = null;
        children.forEach(child => {
          if (child.outlet === 'primary') {
            const snapshot = child.snapshot;
            url += '/' + snapshot.url.map(segment => segment.path).join('/');
            breadcrumbs.push({ label: snapshot.data, url });
            currentRoute = child;
          }
        });
      }
      this.breadcrumbSubject.next([...breadcrumbs]);
    });
  }
}

@Component({
    selector: 'app-breadcrumb',
    template: `
    <ol class="breadcrumb">
      @for (breadcrumb of breadcrumbs | async; track breadcrumb; let last = $last) {
        @if (breadcrumb.label.title && (breadcrumb.url.slice(-1) === '/' || last)) {
          <li
            class="breadcrumb-item"
            [ngClass]="{ active: last }"
            >
            @if (!last) {
              <a [routerLink]="breadcrumb.url">{{ breadcrumb.label.title }}</a>
            }
            @if (last) {
              <span [routerLink]="breadcrumb.url">{{ breadcrumb.label.title }}</span>
            }
          </li>
        }
      }
    </ol>
    `,
    imports: [
        NgClass,
        RouterLink,
        AsyncPipe,
    ],
})
export class AppBreadcrumbComponent {
  constructor(private breadcrumbService: AppBreadcrumbService) {}
  get breadcrumbs() {
    return this.breadcrumbService.breadcrumbs;
  }
}

@NgModule({
    imports: [CommonModule, RouterModule, AppBreadcrumbComponent],
    exports: [AppBreadcrumbComponent],
})
export class AppBreadcrumbModule {
  static forRoot() {
    return {
      ngModule: AppBreadcrumbModule,
      providers: [AppBreadcrumbService],
    };
  }
}

@Component({
    selector: 'app-aside',
    template: `<ng-content></ng-content>`,
})
export class AppAsideComponent implements OnInit, OnDestroy {
  @Input() fixed = false;
  @Input() display: string | boolean = '';
  @HostBinding('class.aside-menu') asideMenuClass = true;
  private readonly fixedClass = 'aside-menu-fixed';
  private displayClass: string | null = null;

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {}

  ngOnInit(): void {
    if (this.fixed) {
      this.renderer.addClass(this.document.body, this.fixedClass);
    }
    this.displayClass = this.getDisplayClass();
    if (this.displayClass) {
      this.renderer.addClass(this.document.body, this.displayClass);
    }
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.fixedClass);
    if (this.displayClass) {
      this.renderer.removeClass(this.document.body, this.displayClass);
    }
  }

  private getDisplayClass(): string | null {
    if (this.display === '' || this.display === null || this.display === undefined) {
      return null;
    }
    if (this.display === true) {
      return 'aside-menu-show';
    }
    if (this.display === false) {
      return null;
    }
    return `aside-menu-${this.display}-show`;
  }
}


